import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useMatches, useNavigate } from 'react-router-dom';
import { GlobalSidebar } from './GlobalSidebar';
import { GlobalToolbar, type RouteHandle } from './GlobalToolbar';
import { MobileAskPush, MobileMore, MobileNews, MobileRail, MobileSearch, MobileTabs, MobileTop, PcOnlyScreen, pcOnlySection } from './MobileChrome';
import { OPEN_EVENT, shouldAskPush } from './pwa';
import { useViewport } from './viewport';
import { Inspector } from './Inspector';
import { SearchOverlay } from './SearchOverlay';
import { InspectorProvider, useInspector } from './InspectorContext';
import { ShellChromeProvider, useShellChrome } from './ShellChrome';
import { Toasts } from '../v3/toast';

/**
 * Трёхзонная композиция (frozen): Global Sidebar → Workspace → Inspector.
 * Горизонтального page-scroll нет: широкий контент скроллится в своих контейнерах.
 * Содержимое Inspector экраны подают через InspectorContext.
 */
export function AppShell() {
  return (
    <ShellChromeProvider>
      <InspectorProvider>
        <AppShellInner />
      </InspectorProvider>
    </ShellChromeProvider>
  );
}

/** Каркас по ширине экрана: телефон и планшет — мобильный (решение Ромы 05.10.2026), дальше — компьютер как был. */
function AppShellInner() {
  const vp = useViewport();
  const navigate = useNavigate();
  // Нажатие на push-уведомление при открытом ПО: помощник (sw.js) просит открыть экран.
  useEffect(() => {
    const onOpen = (e: Event) => { const url = (e as CustomEvent<string>).detail; if (typeof url === 'string' && url.startsWith('/')) navigate(url); };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [navigate]);
  return vp === 'desktop' ? <DesktopShell /> : <MobileShell phone={vp === 'phone'} />;
}

/**
 * Телефон: шапка, рабочая область, капсула меню внизу. Планшет: рейка значков слева, шапка, рабочая область.
 * Разделы «только на компьютере» не загружаются — вместо экрана показывается «… — на компьютере».
 */
function MobileShell({ phone }: { phone: boolean }) {
  const loc = useLocation();
  const pc = pcOnlySection(loc.pathname);
  const detail = useIsDetail() && !pc;
  const [over, setOver] = useState<null | 'more' | 'news' | 'search' | 'push'>(null);
  const close = useCallback(() => setOver(null), []);
  // Переход на другой экран закрывает открытое поверх (меню, поиск, «Что нового»).
  useEffect(() => { setOver((o) => (o === 'push' ? o : null)); }, [loc.pathname, loc.search]);
  // Первый запуск на этом устройстве: один раз спросить про уведомления (потом — в «Ещё»).
  useEffect(() => { let alive = true; void shouldAskPush().then((ask) => { if (alive && ask) setOver((o) => o ?? 'push'); }); return () => { alive = false; }; }, []);
  return (
    <div className={`app-shell m-shell${detail ? ' is-detail' : ''}`}>
      <Toasts />
      {phone ? null : <MobileRail onMore={() => setOver('more')} />}
      <div className="app-main">
        <MobileTop onSearch={() => setOver('search')} onBell={() => setOver('news')} />
        <div className="app-body">
          <main className="workspace">{pc ? <PcOnlyScreen section={pc} /> : <Outlet />}</main>
        </div>
      </div>
      {phone && !detail ? <MobileTabs onMore={() => setOver('more')} /> : null}
      {over === 'more' ? <MobileMore onClose={close} phone={phone} /> : null}
      {over === 'news' ? <MobileNews onClose={close} /> : null}
      {over === 'search' ? <MobileSearch onClose={close} /> : null}
      {over === 'push' ? <MobileAskPush onClose={close} /> : null}
    </div>
  );
}

/** Карточка (заявка, заказ) — экран со своим «назад к списку»: нижнее меню на телефоне скрыто. */
function useIsDetail(): boolean {
  const matches = useMatches();
  const last = [...matches].reverse().find((m) => (m.handle as RouteHandle | undefined)?.title);
  return Boolean((last?.handle as RouteHandle | undefined)?.workspaceTo);
}

function DesktopShell() {
  const inspector = useInspector();
  const { mobileChromeless } = useShellChrome();
  const [searchOpen, setSearchOpen] = useState(false);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className={`app-shell${mobileChromeless ? ' is-mobile-chromeless' : ''}`}>
      <Toasts />
      <GlobalSidebar onOpenSearch={openSearch} />
      <div className="app-main">
        <GlobalToolbar
          inspectorOpen={inspector.open}
          onToggleInspector={inspector.toggle}
          onOpenSearch={openSearch}
        />
        <div className="app-body">
          <main className="workspace">
            <Outlet />
          </main>
          <Inspector
            open={inspector.open}
            onClose={inspector.close}
            category={inspector.category ?? undefined}
          >
            {inspector.content}
          </Inspector>
        </div>
      </div>
      {searchOpen && <SearchOverlay onClose={closeSearch} />}
    </div>
  );
}
